import debug from "debug";

const log = debug("app:bom");



/**
 * Class representing BOM (Bill of Materials) data management.
 * @category Components
 * @subcategory BOM
 */

export class BomData {
  /**
   * Creates an instance of BomData.
   * @param {Object} experience - The experience object containing scene and event hub.
   * @param {Object} experience.scene - The 3D scene to traverse for objects.
   * @param {Object} experience.eventHub - The event hub for emitting and listening to events.
   */
  constructor(experience) {
    this.scene = experience.scene;
    this.eventHub = experience.eventHub;
    this.bomObjectsMap = new Map();
    this.bomTableData = null; // Store BOM data here
    this.updateBomData = this.updateBomData.bind(this);
    this.setupEventListeners();

    /**
     * Fetches data from the data vault based on a request and response event.
     * @param {string} requestEvent - The event to request data.
     * @param {string} id - The ID to send with the request.
     * @param {string} responseEvent - The event to listen for the response.
     * @returns {Promise<Object>} Resolves to a simulated response object containing data.
     */

    this.fetchFromDataVault = (requestEvent, id, responseEvent) => {
      return new Promise((resolve, reject) => {
        const responseHandler = (data) => {
          log(`Received data for ${responseEvent}:`, data);
          resolve({
            ok: true,
            headers: new Headers(),
            json: () => Promise.resolve({ data }), // Mimic the original response structure
          });
          this.eventHub.off(responseEvent, responseHandler); // Clean up the event listener
        };

        this.eventHub.on(responseEvent, responseHandler);
        log(`Emitting ${requestEvent} with id:`, id);
        this.eventHub.emit(requestEvent, id);
      });
    };
  }

  /**
   * Sets up event listeners for the BOM data.
   */

  setupEventListeners() {
    this.eventHub.on("xmd", this.updateBomData);
  }

  cleanup() {
    // 1. Remove event listeners from eventHub
    this.eventHub.off("xmd", this.updateBomData);
  
    // 2. Clear BOM objects map and data
    this.bomObjectsMap.clear();
    this.bomTableData = null;
  
    // 3. Nullify bound functions
    this.updateBomData = null;
    this.fetchFromDataVault = null;
  
    // 4. Remove DOM-level custom event listeners
    document.removeEventListener("bomDataChanged", this.handleBomDataChanged);
  
    // 5. Cancel pending requests if applicable
    if (this.abortController) {
      this.abortController.abort();
    }
    
    console.log("Bom data cleaned");
    const logs = JSON.parse(localStorage.getItem('sessionLogs')) || [];
    logs.push(`[${new Date().toISOString()}] ${"Bom data cleaned"}`);
    localStorage.setItem('sessionLogs', JSON.stringify(logs));
  }

   /**
   * Updates BOM data by traversing the scene and fetching relevant object data.
   * @returns {Promise<void>}
   */

  async updateBomData() {
    log("xmd event received");
    let currentObjects = new Map();
    let traversedObjects = new Set();

    this.scene.traverse((object) => {
      if (
        object.isObject3D &&
        object.userData.pn &&
        !traversedObjects.has(object.uuid) &&
        object.name === "XMO"
      ) {
        traversedObjects.add(object.uuid);
        let { pn, price, mode, properties } = object.userData;

        // For configured parts, use the custom part number from properties
        // This ensures configured parts with the same custom part number are grouped together
        let partNumberForKey = pn;
        if (mode === "cfg-part" && properties && properties["Part Number"]) {
          partNumberForKey = properties["Part Number"];
        } else if (mode === "user-cfg" && properties && properties["Part Number"]) {
          partNumberForKey = properties["Part Number"];
        }

        const key = `${partNumberForKey}-${mode}`;

        if (mode === "cfg-part") {
          if (properties && properties["Price"]) {
            price = properties["Price"];
          } else {
            price = null;
          }
        }

        let existing = currentObjects.get(key);
        let count = existing ? existing.qty : 0;
        
        // Extract additional properties for RFQ
        let name = '';
        let length = '';
        let length_unit = '';
        let color = '';
        
        if (properties) {
          // Try to find name/description from various possible property keys
          name = properties["Name"] || 
                 properties["Description"] || 
                 properties["Part Name"] ||
                 properties["Product Name"] ||
                 '';
          
          // Try to find length from various possible property keys
          const lengthValue = properties["Length"] || 
                             properties["Length (in)"] || 
                             properties["Length(in)"] ||
                             properties["Length (inches)"] ||
                             '';
          length = lengthValue ? String(lengthValue) : '';
          
          // Try to find length unit
          length_unit = properties["Length Unit"] || 
                       properties["Unit"] ||
                       (length ? "in" : ''); // Default to "in" if length exists but no unit specified
          
          // Try to find color
          color = properties["Color"] || 
                 properties["Colour"] ||
                 '';
        }
        
        if (existing) {
          // Increment quantity and merge properties (keep first non-empty value)
          existing.qty = count + 1;
          if (!existing.name && name) existing.name = name;
          if (!existing.length && length) existing.length = length;
          if (!existing.length_unit && length_unit) existing.length_unit = length_unit;
          if (!existing.color && color) existing.color = color;
        } else {
          currentObjects.set(key, {
            partNumber: partNumberForKey,  // Use the correct part number
            qty: count + 1,
            price,
            mode,
            name,
            length,
            length_unit,
            color,
          });
        }
      }
    });

    log("Current Objects after scene traverse: ", currentObjects);
    await this.updateObjectList(currentObjects);
  }

  /**
   * Fetches part details for a given part number from the data vault.
   * @param {string} partNumber - The part number to fetch details for.
   * @returns {Promise<Object|null>} Resolves to part details if available, otherwise null.
   */

  async fetchPartDetails(partNumber) {
    try {
      const partDetailsResponse = await this.fetchFromDataVault(
        "requestPart",
        partNumber,
        "partData"
      );
      const partDetails = (await partDetailsResponse.json()).data;
      return partDetails;
    } catch (error) {
      console.error("Error fetching part details:", error);
      return null;
    }
  }

  /**
   * Updates the BOM object list based on new data.
   * @param {Map} newObjectsMap - A map of new objects with their BOM data.
   * @returns {Promise<void>}
   */

  async updateObjectList(newObjectsMap) {
    let dataChanged = false;

    for (let key of this.bomObjectsMap.keys()) {
      if (!newObjectsMap.has(key)) {
        this.bomObjectsMap.delete(key);
        dataChanged = true;
      }
    }

    for (let [key, value] of newObjectsMap) {
      let { partNumber, qty, price, mode } = value;

      let note = "";
      let nameFromPart = "";
      
      if (mode === "user-cfg" && !price) {
        price = null;
        note = "Request Quote";
      } else if (!price) {
        const partDetails = await this.fetchPartDetails(partNumber);
        if (partDetails) {
          if (partDetails.price) {
            price = partDetails.price;
          }
          // Try to extract name/description from part props if not already set
          if (partDetails.props && Array.isArray(partDetails.props)) {
            const nameProp = partDetails.props.find(prop => 
              prop.label && (
                prop.label.toLowerCase().includes('name') ||
                prop.label.toLowerCase().includes('description') ||
                prop.label.toLowerCase().includes('product')
              )
            );
            if (nameProp && nameProp.value) {
              nameFromPart = nameProp.value;
            }
          }
        }
      }

      if (this.bomObjectsMap.has(key)) {
        let existingPart = this.bomObjectsMap.get(key);
        if (existingPart.qty !== qty || existingPart.each !== price) {
          existingPart.qty = qty;
          existingPart.each = price;
          existingPart.total = price ? price * qty : 0;
          existingPart.note = note;
          // Update name if we got one from part details and existing is empty
          if (!existingPart.name && nameFromPart) {
            existingPart.name = nameFromPart;
          }
          dataChanged = true;
        }
      } else {
        // Preserve additional properties from the new object, prefer name from part details
        const additionalProps = {
          name: nameFromPart || value.name || '',
          length: value.length || '',
          length_unit: value.length_unit || '',
          color: value.color || '',
        };
        
        this.bomObjectsMap.set(key, {
          id: this.bomObjectsMap.size + 1,
          partNumber,
          qty,
          each: price,
          total: price ? price * qty : 0,
          note: note,
          mode,
          ...additionalProps,
        });
        dataChanged = true;
      }
    }

    let index = 1;
    for (let part of this.bomObjectsMap.values()) {
      part.id = index++;
    }

    log("Updated bomObjectsMap: ", this.bomObjectsMap);

    if (dataChanged) {
      this.bomTableData = this.getBomTableData(); // Update persistent BOM data
      log("BOM data changed, emitting update");
      log("this is updated BOM", this.bomTableData);
      this.eventHub.emit("bomUpdated", this.bomTableData);
      const event = new CustomEvent("bomDataChanged", {
        detail: this.bomTableData,
      });
      document.dispatchEvent(event);
    }
  }

  /**
   * Generates BOM table data for display.
   * @returns {Object} An object containing the header, data, and totals of the BOM table.
   */

  getBomTableData() {
    const header = {
      ID: "id",
      "Part Number": "partNumber",
      QTY: "qty",
      Each: "each",
      Total: "total",
      Note: "note",
    };
    const data = Array.from(this.bomObjectsMap.values());
    const total = this.calculateTotals(data);
    return { header, data, total };
  }

  /**
   * Calculates totals for the BOM data.
   * @param {Array<Object>} data - The array of BOM data items.
   * @returns {Array} An array containing the total value, and note for the BOM.
   */

  calculateTotals(data) {
    const totalValue = data.reduce((sum, item) => sum + item.total, 0);
    const allPricesAvailable = data.every((item) => item.each);
    const note = allPricesAvailable ? "" : "Request Quote";
    return ["Estimated (USD)", totalValue, note];
  }

  /**
   * Retrieves the BOM parts in a structured format.
   * @returns {Object} An object containing the BOM parts data and totals.
   */

  getBomParts() {
    const bomTableData = this.bomTableData || this.getBomTableData();
    const bomParts = {
      data: bomTableData.data.map((part) => ({
        id: part.id,
        partNumber: part.partNumber,
        qty: part.qty,
        each: part.each,
        total: part.total,
        note: part.note,
        // Include additional properties if available
        name: part.name || '',
        length: part.length || '',
        length_unit: part.length_unit || '',
        color: part.color || '',
      })),
      total: bomTableData.total,
    };
    return bomParts;
  }
}
